// PRD.275 — Elia AI Assistant Drawer (Phase 1, UI only).
//
// Deterministic, frontend-only chat client. All timing and response-selection
// logic lives HERE (not in the React components) so a real Doctor Consult
// client can drop in behind the same `EliaChatClient` contract later.

import type { EliaChatClient, EliaChatResponse } from './elia-chat-types';
import {
  ELIA_CHECKLIST_CARD,
  ELIA_FOLLOWUP_SUGGESTIONS,
  ELIA_HEADACHE_RESPONSE,
} from './elia-chat-fixtures';

export function normalizeQuery(query: string): string {
  return query.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Maps an incoming query to a deterministic fixture response. Pure — no timing.
 * Exposed for direct unit testing.
 */
export function matchEliaResponse(query: string): EliaChatResponse {
  const q = normalizeQuery(query);

  if (q.includes('headache') || q.includes('what should i check next')) {
    return {
      response: ELIA_HEADACHE_RESPONSE,
      card: ELIA_CHECKLIST_CARD,
      suggestions: ELIA_FOLLOWUP_SUGGESTIONS,
    };
  }

  if (q.includes('information is missing') || q.includes('what information')) {
    return {
      response:
        "A few details are not documented yet: the headache's severity and exact " +
        'location, whether the onset was sudden, the current blood pressure, and ' +
        'any visual changes, fever, or neck stiffness. Confirming these will help ' +
        'complete the picture.',
      suggestions: ELIA_FOLLOWUP_SUGGESTIONS,
    };
  }

  if (q.includes('possible diagnosis') || q.includes('explain the possible')) {
    return {
      response:
        'With a three-day headache and the findings recorded so far, several ' +
        'everyday explanations remain possible, such as a tension-type headache, ' +
        'a migraine, or a headache related to blood-pressure changes. The record ' +
        'does not yet contain enough detail to favour one. The checks above will ' +
        'help tell them apart.',
      suggestions: ELIA_FOLLOWUP_SUGGESTIONS,
    };
  }

  if (q.includes('summarize') || q.includes('summary')) {
    return {
      response:
        "Today's visit is a consultation for Jessica Wong. The main concern is a " +
        'headache lasting three days. Key findings such as blood pressure, onset ' +
        'pattern, and neurological signs are not documented yet.',
      suggestions: ELIA_FOLLOWUP_SUGGESTIONS,
    };
  }

  if (q === 'why?' || q.startsWith('why ') || q === 'why') {
    return {
      response:
        'These checks matter because a headache lasting a few days is usually ' +
        'harmless, but a small number of causes are serious. Blood pressure, a ' +
        'sudden severe onset, visual changes, weakness or numbness, fever with ' +
        'neck stiffness, and a recent head injury are the findings that would ' +
        'change your plan.',
      suggestions: ELIA_FOLLOWUP_SUGGESTIONS,
    };
  }

  if (q.includes('red flag')) {
    return {
      response:
        'The main red flags for a headache are: a sudden "worst-ever" onset, a ' +
        'very high blood-pressure reading, new visual changes, weakness or ' +
        'numbness on one side, fever combined with neck stiffness, and any recent ' +
        'head injury. Any of these would warrant more urgent assessment.',
      suggestions: ELIA_FOLLOWUP_SUGGESTIONS,
    };
  }

  if (q.includes('ask the patient') || q.includes('what should i ask')) {
    return {
      response:
        'Helpful questions include: When exactly did it start, and did it come on ' +
        'suddenly? Where is the pain and what does it feel like? Anything that ' +
        'makes it better or worse? Any visual changes, nausea, fever, or neck ' +
        'stiffness? Any recent bump or injury to the head?',
      suggestions: ELIA_FOLLOWUP_SUGGESTIONS,
    };
  }

  return {
    response:
      "I can help you think through Jessica's consultation. Try asking what to " +
      'check next, what information is missing, or for a short summary of ' +
      "today's visit.",
    suggestions: ELIA_FOLLOWUP_SUGGESTIONS,
  };
}

/** Default failure trigger — any query containing the word "fail". */
export function defaultFailWhen(query: string): boolean {
  return normalizeQuery(query).includes('fail');
}

export type MockEliaChatClientOptions = {
  /** Response delay in ms. Use 0 in tests. Defaults to a natural UX delay. */
  delayMs?: number;
  /** Controllable failure path. Reject when this returns true. */
  failWhen?: (query: string) => boolean;
};

export function createMockEliaChatClient(options: MockEliaChatClientOptions = {}): EliaChatClient {
  const { delayMs = 850, failWhen = defaultFailWhen } = options;

  return {
    sendMessage({ query }) {
      return new Promise((resolve, reject) => {
        const settle = () => {
          if (failWhen(query)) {
            reject(new Error('ELIA_MOCK_FAILURE'));
            return;
          }
          resolve(matchEliaResponse(query));
        };

        if (delayMs <= 0) {
          settle();
        } else {
          setTimeout(settle, delayMs);
        }
      });
    },
  };
}
